/**
 * `/settings/integrations/*` — #44574.
 *
 * Ports of the integrations hub pages that were unmounted on the Vite tree.
 * Live Next pages under excellent/app/settings/integrations/** are UNTOUCHED.
 *
 * Mutations already have Hono twins (RO-2-02). This file mounts the client
 * shells with SettingsPageShell + self-fetching islands where contracts exist.
 */

import * as React from "react";
import { createRoute } from "@tanstack/react-router";
import {
  Button,
  DataTable,
  EmptyState,
  SettingsPageShell,
  SettingsSection,
  buttonVariants,
} from "@excellent/ui";
import { apiCall, useApi } from "@excellent/ui/api";
import { settingsIntegrations } from "@excellent/ui/api/contracts/settings-integrations";
import { verificationEvidence } from "@excellent/ui/api/contracts/verification-evidence";
import { directorySetup } from "@excellent/ui/api/contracts/directory-setup";
import { SourceCoverageApproval } from "../verification/source-coverage-approval";
import { SourceCompilerReview } from "@excellent/ui/blocks/excellent/source-compiler-review";
import { DirectorySetup } from "../shared/settings/directory-setup";
import { ConflictsQueue } from "../shared/settings/conflicts-queue";
import { rootRoute } from "./__root";

// ── /settings/integrations ──────────────────────────────────────────────────

function IntegrationsHome() {
  const [seedMsg, setSeedMsg] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  async function onSeed() {
    setBusy(true);
    setSeedMsg(null);
    try {
      const r = await apiCall(settingsIntegrations.applySeed, {});
      setSeedMsg(r.ok ? `Seeded ${r.providers ?? 0} provider(s).` : "Seed failed.");
    } catch (e) {
      setSeedMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-integrations-setup>
    <SettingsPageShell
      title="Integrations"
      description="Connect the tools you already use. Direct OAuth + encrypted local token store — no external broker."
    >
      <div className="mb-4 flex flex-wrap gap-2">
        <Button type="button" disabled={busy} onClick={() => void onSeed()}>
          {busy ? "Seeding…" : "Seed catalog"}
        </Button>
        <a
          href="/settings/integrations/sources/new"
          className={buttonVariants({ variant: "outline" })}
        >
          Add custom source
        </a>
        <a
          href="/settings/integrations/database"
          className={buttonVariants({ variant: "outline" })}
        >
          Connect a database
        </a>
        <a href="/settings/integrations/conflicts" className={buttonVariants({ variant: "ghost" })}>
          Sync conflicts
        </a>
        <a href="/settings/integrations/activity" className={buttonVariants({ variant: "ghost" })}>
          Sync activity
        </a>
        <a href="/settings/integrations/settings" className={buttonVariants({ variant: "ghost" })}>
          Settings
        </a>
      </div>
      {seedMsg ? <p className="mb-4 type-body text-muted-foreground">{seedMsg}</p> : null}
      <nav aria-label="Verification results" className="mb-6 flex flex-wrap gap-4 type-body">
        <a className="underline" href="/receipts">View signed verdicts and evidence</a>
        <a className="underline" href="/work-ledger">Review work and repair history</a>
      </nav>
      <div className="mb-6 space-y-4">
        <details className="rounded-lg border p-4">
          <summary className="cursor-pointer type-body font-medium">Workspace approvals and Environment access</summary>
          <p className="my-3 type-body text-muted-foreground">Review approval keys, enrollment and repository permissions. Each approval keeps its own scope and confirmation.</p>
          <DirectorySetup />
        </details>
        <details className="rounded-lg border p-4">
          <summary className="cursor-pointer type-body font-medium">Source intake and contract checks</summary>
          <p className="my-3 type-body text-muted-foreground">Select authorized sources, reopen saved reviews and inspect contract acceptance.</p>
          <SourceCompilerReview renderCoverage={selection => <AuthenticatedSourceCoverage key={`${selection.scenario}:${selection.compilationId}:${selection.expectedCompilationDigest}`} selection={selection} />} />
        </details>
        <details className="rounded-lg border p-4">
          <summary className="cursor-pointer type-body font-medium">Connector capabilities and oracle adapters</summary>
          <div className="mt-4"><CapabilityRegistryRoutingPanel /></div>
        </details>
        <details className="rounded-lg border p-4">
          <summary className="cursor-pointer type-body font-medium">Source intent example · illustrative only</summary>
          <div className="mt-4"><SourceIntentInferencePanel /></div>
        </details>
      </div>
      <EmptyState
        title="No providers yet"
        description="Seed the catalog or add a custom source to connect your first integration. Connected accounts appear here with sync controls."
      />
    </SettingsPageShell>
    </div>
  );
}

/** Credentials are read from the authenticated Directory, within the compiler's private cache. */
function AuthenticatedSourceCoverage({ selection }: {
  selection: React.ComponentProps<typeof SourceCoverageApproval>["selection"];
}) {
  const status = useApi(directorySetup.status, { body: {} }, {
    refreshInterval: 15_000, revalidateOnFocus: true, shouldRetryOnError: false,
  });
  if (status.error) return <p role="alert">Source coverage approval credentials are unavailable. {status.error.message}</p>;
  if (!status.data) return <p role="status">Checking source coverage approval credentials…</p>;
  return <SourceCoverageApproval selection={selection} credentials={status.data.credentials} onApproved={() => {
    // The ceremony records coverage only. Host review and contract acceptance
    // independently recheck current signed authority on their server paths.
  }} />;
}

function CapabilityRegistryRoutingPanel() {
  const { data, error, isLoading } = useApi(verificationEvidence.connectorsCatalog, {
    query: { env: "real" },
  });
  const {
    data: managementData,
    error: managementError,
    isLoading: managementLoading,
  } = useApi(verificationEvidence.capabilityRegistryManagementList, {
    query: { env: "real" },
  });
  const [managementResult, setManagementResult] = React.useState<string | null>(null);
  const [managementBusy, setManagementBusy] = React.useState(false);
  const items = data?.items ?? [];
  const managedItems = managementData?.items ?? [];

  async function installGithubRegistryAdapter() {
    setManagementBusy(true);
    setManagementResult(null);
    try {
      const response = await apiCall(verificationEvidence.capabilityRegistryManage, {
        query: { env: "real" },
        body: {
          actorId: "real:act_matt",
          provider: "github",
          action: "install_oracle",
          oracleAdapter: "oracle:git-tree",
        },
      });
      setManagementResult(
        `${response.management.provider} ${response.management.oracleAdapter} ${response.management.state}; verdict remains ${response.verdictStatus}`,
      );
    } catch (e) {
      setManagementResult(e instanceof Error ? e.message : String(e));
    } finally {
      setManagementBusy(false);
    }
  }

  return (
    <SettingsSection
      id="capability-registry-routing"
      title="Capability registry routing"
      description="Capabilities are routing inputs, not verdicts."
    >
      <div
        className="space-y-3 type-body"
        data-cu107-capability-registry-routing="verificationEvidence.connectorsCatalog"
        data-cu107-capability-source="verificationEvidence.connectorsCatalog"
      >
        <p className="text-muted-foreground">
          Connector capabilities feed the collector, evaluator, and oracle registry. A predicate
          reaches verification only when its domain pack, pinned evaluator digest, environment
          capability, and authenticated installed or delegated oracle adapter all resolve.
        </p>
        <p className="text-muted-foreground">
          missing capability coverage remains a named <code>NOT_RUN</code> planned gap. This catalog
          is read-only and never emits a PASS verdict by itself.
        </p>
        <div
          className="rounded-md border p-3"
          data-cu107-live-registry-management
          data-cu107-live-registry-contract="verificationEvidence.capabilityRegistryManage"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="font-medium">Live registry management</p>
              <p className="mt-1 text-muted-foreground">
                Installing or delegating an oracle adapter updates the managed routing registry for
                future collector/evaluator/oracle resolution. It records adapter state only; it
                still returns <code>NOT_RUN</code> until an obligation executes with admitted
                evidence.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              disabled={managementBusy}
              onClick={() => void installGithubRegistryAdapter()}
              data-cu107-install-github-oracle
            >
              {managementBusy ? "Installing…" : "Install GitHub oracle"}
            </Button>
          </div>
          {managementLoading ? (
            <p className="mt-2 text-muted-foreground">Reading managed registry state…</p>
          ) : null}
          {managementError ? (
            <p className="mt-2 text-destructive">
              Managed registry unavailable:{" "}
              {managementError instanceof Error ? managementError.message : String(managementError)}
            </p>
          ) : null}
          {managementResult ? (
            <p className="mt-2 text-muted-foreground" data-cu107-management-result>
              {managementResult}
            </p>
          ) : null}
          {managedItems.length ? (
            <ul className="mt-2 list-disc pl-5 text-muted-foreground">
              {managedItems.map((item) => (
                <li
                  key={item.id}
                  data-cu107-managed-oracle={item.oracleAdapter}
                  data-cu107-managed-state={item.state}
                >
                  {item.provider}: {item.oracleAdapter} is {item.state}; verdict{" "}
                  {item.verdictStatus}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-muted-foreground" data-cu107-management-empty>
              No oracle adapters have been installed, delegated, or disabled in the managed registry
              yet.
            </p>
          )}
        </div>
        {isLoading ? (
          <p className="text-muted-foreground">Loading connector capabilities…</p>
        ) : null}
        {error ? (
          <p className="text-destructive">
            Capability registry unavailable:{" "}
            {error instanceof Error ? error.message : String(error)}
          </p>
        ) : null}
        {items.length ? (
          <DataTable
            wrapInCard={false}
            rows={items}
            rowKey={(item) => item.provider}
            rowAttributes={(item) => ({
              "data-cu107-capability-provider": item.provider,
              "data-cu107-capability-family": item.family,
              "data-cu107-capability-read-only": String(item.readOnly),
            })}
            columns={[
              {
                key: "provider",
                header: "Provider",
                render: (item) => item.displayName,
              },
              { key: "family", header: "Family", render: (item) => item.family },
              {
                key: "capabilities",
                header: "Capabilities",
                render: (item) => item.capabilities.join(", "),
              },
              {
                key: "requiredScopes",
                header: "Required scopes",
                render: (item) => item.requiredScopes.join(", "),
              },
            ]}
          />
        ) : !isLoading && !error ? (
          <p className="text-muted-foreground">
            No connector capabilities are registered for this environment.
          </p>
        ) : null}
      </div>
    </SettingsSection>
  );
}

function SourceIntentInferencePanel() {
  const { data, error, isLoading } = useApi(verificationEvidence.sourceIntentGet, {
    query: { env: "real", scenario: "workflow-template-observed-occurrence" },
  });
  const citedSourceCount = new Set([
    ...(data?.outcomes.flatMap((outcome) => outcome.citedSourceIds) ?? []),
    ...(data?.alternatives.flatMap((alternative) => alternative.citedSourceIds) ?? []),
  ]).size;

  return (
    <SettingsSection
      id="source-backed-intent"
      title="Source-backed intent example"
      description="Illustrates source-based intent inference. Live source grants and revocation are not connected to this example."
    >
      <div data-cu104-source-intent-live-path className="grid gap-3">
        {isLoading ? (
          <p className="type-body text-muted-foreground">Reading source-intent inference…</p>
        ) : error ? (
          <p className="type-body text-destructive">{error.message}</p>
        ) : data ? (
          <>
            <p
              data-cu104-source-intent-origin={data.evidenceOrigin}
              className="type-subtext text-muted-foreground"
            >
              {data.evidenceOrigin === "session_demo_intake"
                ? "Session-only demo intake — digests are checked; permission labels are supplied by the caller and are not authorization."
                : "Synthetic fallback scenario — no live source observation was performed."}
            </p>
            <div className="rounded-lg border p-3">
              <p className="type-label text-muted-foreground">Decision</p>
              <p data-cu104-source-intent-decision className="type-body font-medium">
                {data.decision.toUpperCase()} — {data.engineVersion}
              </p>
              <p className="mt-1 type-subtext text-muted-foreground">
                Scenario: {data.scenario}; cited source count: {citedSourceCount}; observation
                allowed: {data.observationAllowed ? "yes" : "no"}
              </p>
            </div>
            <div className="grid gap-2 md:grid-cols-2">
              <div className="rounded-lg border p-3">
                <p className="type-label text-muted-foreground">Source-backed outcomes</p>
                {data.outcomes.length > 0 ? (
                  <ul className="mt-2 list-disc pl-5 type-subtext text-muted-foreground">
                    {data.outcomes.map((outcome) => (
                      <li key={outcome.id} data-cu104-source-intent-outcome>
                        {outcome.statement} Sources: {outcome.citedSourceIds.join(", ")}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <ul className="mt-2 list-disc pl-5 type-subtext text-muted-foreground">
                    {data.alternatives.map((alternative) => (
                      <li key={alternative.id} data-cu104-source-intent-alternative>
                        {alternative.statement}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="rounded-lg border p-3">
                <p className="type-label text-muted-foreground">Permission limit</p>
                <p
                  data-cu104-source-intent-permission
                  className="mt-2 type-subtext text-muted-foreground"
                >
                  {data.permissionLimit ?? "All cited sources are content-authorized."}
                </p>
                <p className="mt-2 type-subtext text-muted-foreground">
                  {data.retainedArtifactPolicy}
                </p>
              </div>
            </div>
            <div className="rounded-lg border p-3">
              <p className="type-label text-muted-foreground">Source precedence</p>
              <p
                data-cu104-source-intent-precedence
                className="mt-2 type-subtext text-muted-foreground"
              >
                {data.sourcePrecedence.join(" → ")}
              </p>
            </div>
          </>
        ) : null}
      </div>
    </SettingsSection>
  );
}

// ── /settings/integrations/activity ─────────────────────────────────────────

function IntegrationsActivity() {
  return (
    <SettingsPageShell
      title="Sync activity"
      description="Recent sync runs across every connection — success, partial, and failed."
    >
      <EmptyState
        title="No sync activity yet"
        description="Once a connection runs a sync, rows land here with status, counts, and duration."
      />
    </SettingsPageShell>
  );
}

// ── /settings/integrations/conflicts ────────────────────────────────────────

function IntegrationsConflicts() {
  return (
    <SettingsPageShell
      title="Sync conflicts"
      description="A synced value diverged from one you (or another source) had. High-stakes fields are surfaced here instead of being overwritten — keep yours, accept the synced value, or merge."
    >
      <ConflictsQueue />
    </SettingsPageShell>
  );
}

// ── /settings/integrations/database ─────────────────────────────────────────

function IntegrationsDatabase() {
  return (
    <SettingsPageShell
      title="Connect a database"
      description="Pull leads, contacts and companies straight out of Supabase or any PostgREST database. We scan your tables and guess what's what — you confirm."
    >
      <SettingsSection
        id="wizard"
        title="Database wizard"
        description="Connect → classify → map → import. Mutations use the settings-integrations-database contract on Hono."
      >
        <p className="type-body text-muted-foreground">
          Open the full wizard from the Next host for multi-step PostgREST import, or use the
          MCP/API twin <code>/api/settings/integrations/database/*</code> from agents.
        </p>
        <p className="mt-2 type-body">
          <a className="underline" href="/settings/integrations/sources/new">
            Prefer a custom HTTP source?
          </a>
        </p>
      </SettingsSection>
    </SettingsPageShell>
  );
}

// ── /settings/integrations/settings ─────────────────────────────────────────

function IntegrationsSettingsPage() {
  const [pollerMsg, setPollerMsg] = React.useState<string | null>(null);

  async function runDue() {
    try {
      await apiCall(settingsIntegrations.runDueNow, {});
      setPollerMsg("Due syncs triggered.");
    } catch (e) {
      setPollerMsg(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <SettingsPageShell
      title="Integration settings"
      description="Direct OAuth + encrypted local token store. No external service."
    >
      <SettingsSection
        id="encryption"
        title="Encryption"
        description="Tokens are sealed AES-256-GCM before they touch SQLite. The key is EXCELLENT_ENCRYPTION_KEY (env) or an OS keychain entry."
      >
        <p className="type-body text-muted-foreground">
          Without a key, new credentials cannot be stored. Rotate only with a deliberate key re-seal
          procedure.
        </p>
      </SettingsSection>
      <SettingsSection
        id="poller"
        title="Background poller"
        description="Runs due syncs on a schedule for connections with auto-sync enabled."
      >
        <Button type="button" variant="outline" onClick={() => void runDue()}>
          Run due now
        </Button>
        {pollerMsg ? <p className="mt-2 type-body text-muted-foreground">{pollerMsg}</p> : null}
      </SettingsSection>
    </SettingsPageShell>
  );
}

// ── /settings/integrations/sources/new ──────────────────────────────────────

function IntegrationsSourcesNew() {
  return (
    <SettingsPageShell
      title="Add a custom source"
      description="Connect any HTTP API or upload an export file — no code. We land the raw records first; you design the mapping to your objects next."
    >
      <SettingsSection
        id="wizard"
        title="Source wizard"
        description="Basics → connect → models → review. Writes a provider_defs row via the sources adapter."
      >
        <p className="type-body text-muted-foreground">
          Use the API twin <code>/api/settings/integrations/sources</code> to register a source,
          then open the connection’s Mapping and Raw records pages.
        </p>
      </SettingsSection>
    </SettingsPageShell>
  );
}

// ── /settings/integrations/connections/$id/mapping ──────────────────────────

function IntegrationsMapping() {
  const { id } = settingsIntegrationsMappingRoute.useParams();
  return (
    <SettingsPageShell
      title="Mapping"
      description={`Design how connection #${id}'s raw records map onto object kinds.`}
    >
      <SettingsSection
        id="designer"
        title="Mapping designer"
        description="Pick a model, inspect a sample raw record, and map fields onto a target kind."
      >
        <p className="type-body text-muted-foreground">
          Mapping mutations use <code>/api/settings/integrations/mapping</code>. Open this
          connection on the Next host for the full sample-record designer, or drive the contract
          from an agent.
        </p>
        <p className="mt-2 type-body">
          <a className="underline" href={`/settings/integrations/connections/${id}/records`}>
            Browse raw records
          </a>
        </p>
      </SettingsSection>
    </SettingsPageShell>
  );
}

// ── /settings/integrations/connections/$id/records ──────────────────────────

function IntegrationsRecords() {
  const { id } = settingsIntegrationsRecordsRoute.useParams();
  return (
    <SettingsPageShell
      title="Raw records"
      description={`Every external record connection #${id} landed, whether or not it mapped onto an object.`}
    >
      <SettingsSection
        id="records"
        title="Records browser"
        description="Per-row JSON drawer and mapped/unmapped filter. Intake batches use model=intake-&lt;uid&gt;."
      >
        <p className="type-body text-muted-foreground">
          Raw record listing is served by the integrations sources/data layer on the Next host. This
          route is mounted so redirects and deep links from intake no longer 404 on the Vite tree.
        </p>
        <p className="mt-2 type-body">
          <a className="underline" href={`/settings/integrations/connections/${id}/mapping`}>
            Design mapping
          </a>
        </p>
      </SettingsSection>
    </SettingsPageShell>
  );
}

export const settingsIntegrationsHomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/integrations",
  component: IntegrationsHome,
});

export const settingsIntegrationsActivityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/integrations/activity",
  component: IntegrationsActivity,
});

export const settingsIntegrationsConflictsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/integrations/conflicts",
  component: IntegrationsConflicts,
});

export const settingsIntegrationsDatabaseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/integrations/database",
  component: IntegrationsDatabase,
});

export const settingsIntegrationsSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/integrations/settings",
  component: IntegrationsSettingsPage,
});

export const settingsIntegrationsSourcesNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/integrations/sources/new",
  component: IntegrationsSourcesNew,
});

export const settingsIntegrationsMappingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/integrations/connections/$id/mapping",
  component: IntegrationsMapping,
});

export const settingsIntegrationsRecordsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/integrations/connections/$id/records",
  component: IntegrationsRecords,
});
